import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { stripePromise } from '@/lib/stripe';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Check, ChevronLeft, ChevronRight, ImageIcon, Loader2, Lock, ExternalLink, BarChart3,
} from 'lucide-react';
import { API } from '@/App';
import { SpotlightCreative } from '@/components/spotlights/SpotlightCreative';
import { isMobileBuild } from '@/lib/mobileBuild';
import UpgradeOnWebNotice from '@/components/UpgradeOnWebNotice';

const STEPS = ['You', 'Artwork', 'Words', 'Destination', 'Review'];

function PayForm({ amount, onPaid }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}${window.location.pathname}` },
      redirect: 'if_required',
    });
    if (error) {
      toast.error(error.message);
      setProcessing(false);
      return;
    }
    if (paymentIntent?.status === 'succeeded') onPaid();
    else {
      toast.error('Payment did not complete');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="advertiser-pay-form">
      <div className="mb-5 rounded-2xl border border-white/10 bg-white/5 p-4">
        <PaymentElement options={{ layout: 'tabs' }} />
      </div>
      <Button
        type="submit"
        disabled={!stripe || processing}
        className="w-full bg-amber-400 py-6 text-slate-900 transition-colors duration-200 hover:bg-amber-300"
        data-testid="advertiser-pay-now-btn"
      >
        {processing
          ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Processing…</>
          : <><Lock className="mr-2 h-4 w-4" /> Pay ${amount?.toFixed(2)} and submit</>}
      </Button>
    </form>
  );
}

export default function AdvertiserFormPage() {
  const { token } = useParams();
  const [invite, setInvite] = useState(null);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [done, setDone] = useState(false);
  // His own test ad went live with a truncated image link and showed a broken box on the
  // Marketplace. The preview now tells the advertiser before it ever gets that far.
  const [imageBad, setImageBad] = useState(false);

  const [form, setForm] = useState({
    contact_name: '', contact_email: '', company: '',
    title: '', body: '', image_url: '',
    cta_label: 'Learn more', cta_type: 'external', external_url: '', link_path: '',
  });

  const load = async () => {
    try {
      const res = await axios.get(`${API}/advertise/${token}`);
      setInvite(res.data);
      setStats(res.data.stats);
      const sub = res.data.submission;
      setForm((prev) => ({
        ...prev,
        contact_name: prev.contact_name || res.data.advertiser_name || '',
        company: prev.company || res.data.company || '',
        ...(sub ? {
          title: sub.title || '', body: sub.body || '', image_url: sub.image_url || '',
          cta_label: sub.cta_label || 'Learn more', cta_type: sub.cta_type || 'external',
          external_url: sub.external_url || '', link_path: sub.link_path || '',
        } : {}),
      }));
      if (sub && ['paid_pending_approval', 'approved'].includes(sub.status)) setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'This link is not valid');
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const spec = invite?.slot?.image;
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  useEffect(() => { setImageBad(false); }, [form.image_url]);

  const stepValid = useMemo(() => {
    if (!invite) return false;
    if (step === 0) return form.contact_name.trim() && /\S+@\S+\.\S+/.test(form.contact_email);
    if (step === 1) return true;
    if (step === 2) return form.title.trim().length >= 3 && form.cta_label.trim();
    if (step === 3) {
      return form.cta_type === 'internal'
        ? form.link_path.startsWith('/')
        : /^https?:\/\/.+\..+/.test(form.external_url.trim());
    }
    return true;
  }, [step, form, invite]);

  const submit = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/advertise/${token}/submit`, form);
      if (res.data.client_secret) {
        setClientSecret(res.data.client_secret);
        setStep(5);
      } else {
        setDone(true);
        toast.success('Submitted — we will review it shortly');
        load();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit your ad');
    } finally {
      setSaving(false);
    }
  };

  const afterPayment = async () => {
    try {
      await axios.post(`${API}/advertise/${token}/paid`);
    } catch { /* the webhook will catch it */ }
    setDone(true);
    toast.success('Payment received — your ad is with our team for approval');
    load();
  };

  if (error) {
    return (
      <Shell>
        <h1 className="mb-4 font-serif text-4xl text-white">We can&apos;t open that link</h1>
        <p className="text-base text-gray-300" data-testid="advertiser-link-error">{error}</p>
      </Shell>
    );
  }
  if (!invite) {
    return <Shell><Loader2 className="h-6 w-6 animate-spin text-amber-300" /></Shell>;
  }

  if (done) {
    const sub = invite.submission || {};
    const live = sub.status === 'approved';
    return (
      <Shell>
        <div data-testid="advertiser-submitted">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-4 py-1.5 text-sm text-emerald-200">
            <Check className="h-4 w-4" />
            {live ? 'Your ad is live' : 'Received — awaiting approval'}
          </div>
          <h1 className="mb-4 font-serif text-4xl text-white sm:text-5xl">
            {live ? 'You’re on the wall' : 'Thank you — we have it'}
          </h1>
          <p className="mb-10 text-base text-gray-300">
            {live
              ? 'Your ad is showing to members now. Here is how it is doing.'
              : 'Our team reviews every ad by hand. If we need anything changed we will email you, and this same link will reopen your form with everything still in it.'}
          </p>

          {invite.admin_note && (
            <div className="mb-8 rounded-2xl border border-amber-300/25 bg-amber-400/10 p-5" data-testid="advertiser-admin-note">
              <p className="mb-1 text-xs uppercase tracking-[0.2em] text-amber-200">A note from our team</p>
              <p className="text-sm text-gray-200">{invite.admin_note}</p>
            </div>
          )}

          {stats && (
            <div className="mb-10 grid grid-cols-3 gap-4" data-testid="advertiser-stats">
              <Stat label="Times seen" value={stats.impressions} />
              <Stat label="Clicks" value={stats.clicks} />
              <Stat label="Click rate" value={`${stats.ctr}%`} icon={<BarChart3 className="h-4 w-4" />} />
            </div>
          )}

          <div className="mb-10 max-w-md">
            <p className="mb-3 text-xs uppercase tracking-[0.2em] text-white/40">Your ad</p>
            <SpotlightCreative p={form} variant={invite.slot.variant} theme={invite.slot.theme || 'dark'} />
          </div>

          {!live && (
            <Button
              variant="outline"
              onClick={() => { setDone(false); setStep(0); }}
              className="border-white/20 bg-white/5 text-white transition-colors duration-200 hover:bg-white/15"
              data-testid="advertiser-edit-again-btn"
            >
              Make a change
            </Button>
          )}
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <p className="mb-3 text-xs uppercase tracking-[0.3em] text-amber-300/80">
        {invite.slot.label} · {invite.run_label}
      </p>
      <h1 className="mb-3 font-serif text-4xl text-white sm:text-5xl">Build your ad</h1>
      <p className="mb-10 max-w-xl text-base text-gray-300">
        Five short steps, about five minutes. Nothing appears anywhere until our team has approved
        it, and you can change it before then.
      </p>

      {/* progress */}
      <div className="mb-10 flex flex-wrap items-center gap-2" data-testid="advertiser-steps">
        {STEPS.map((label, i) => (
          <div
            key={label}
            className={`flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs transition-colors duration-200 ${
              i === step ? 'bg-amber-400 text-slate-900'
                : i < step ? 'bg-white/10 text-white' : 'bg-white/5 text-white/40'
            }`}
          >
            {i < step ? <Check className="h-3 w-3" /> : <span>{i + 1}</span>}
            {label}
          </div>
        ))}
      </div>

      <div className="grid gap-12 lg:grid-cols-[1fr_360px]">
        <div>
          {step === 0 && (
            <Section title="Who is this ad for?" hint="We only use this to reach you about the ad itself.">
              <Field label="Your name">
                <Input value={form.contact_name} onChange={set('contact_name')}
                       data-testid="advertiser-name-input" placeholder="Jane Smith" />
              </Field>
              <Field label="Your email">
                <Input type="email" value={form.contact_email} onChange={set('contact_email')}
                       data-testid="advertiser-email-input" placeholder="jane@example.com" />
              </Field>
              <Field label="Business name" optional>
                <Input value={form.company} onChange={set('company')}
                       data-testid="advertiser-company-input" placeholder="Smith Botanicals" />
              </Field>
            </Section>
          )}

          {step === 1 && (
            <Section
              title="Your artwork"
              hint={spec ? `This spot wants ${spec.width} × ${spec.height} pixels (${spec.ratio}), under ${spec.max_kb}KB. ${spec.note}` : ''}
            >
              <Field label="Image address" optional>
                <Input value={form.image_url} onChange={set('image_url')}
                       data-testid="advertiser-image-input"
                       placeholder="https://… a direct link to your image" />
              </Field>
              <p className="text-sm text-white/45">
                Paste a link to your image and the preview on the right updates immediately. No
                image is fine too — your words will fill the space.
              </p>
            </Section>
          )}

          {step === 2 && (
            <Section title="What should it say?" hint="Short beats clever. Members are scanning.">
              <Field label={`Headline (${form.title.length}/80)`}>
                <Input value={form.title} onChange={set('title')} maxLength={80}
                       data-testid="advertiser-title-input" placeholder="Hand-blended flower essences" />
              </Field>
              <Field label={`Supporting line (${form.body.length}/240)`} optional>
                <Textarea value={form.body} onChange={set('body')} maxLength={240} rows={3}
                          data-testid="advertiser-body-input"
                          placeholder="Made in small batches in Colorado. Free shipping over $60." />
              </Field>
              <Field label="Button text">
                <Input value={form.cta_label} onChange={set('cta_label')} maxLength={28}
                       data-testid="advertiser-cta-label-input" placeholder="Shop the collection" />
              </Field>
            </Section>
          )}

          {step === 3 && (
            <Section title="Where should the button take them?">
              <div className="mb-5 flex flex-wrap gap-2">
                <Choice active={form.cta_type === 'external'}
                        onClick={() => setForm((f) => ({ ...f, cta_type: 'external' }))}
                        testid="advertiser-dest-external">My own website</Choice>
                {invite.allow_internal && (
                  <Choice active={form.cta_type === 'internal'}
                          onClick={() => setForm((f) => ({ ...f, cta_type: 'internal' }))}
                          testid="advertiser-dest-internal">My page on 11/11</Choice>
                )}
              </div>
              {form.cta_type === 'external' ? (
                <Field label="Your web address">
                  <Input value={form.external_url} onChange={set('external_url')}
                         data-testid="advertiser-external-url-input"
                         placeholder="https://smithbotanicals.com/essences" />
                </Field>
              ) : (
                <Field label="Your page here" hint="For example /dashboard/marketplace/store/your-store">
                  <Input value={form.link_path} onChange={set('link_path')}
                         data-testid="advertiser-link-path-input" placeholder="/dashboard/marketplace/store/…" />
                </Field>
              )}
              <p className="text-sm text-white/45">
                Links to your own website open in a new tab, so members don&apos;t lose their place.
              </p>
            </Section>
          )}

          {step === 4 && (
            <Section title="Have a last look" hint="This is exactly what members will see.">
              <dl className="mb-8 space-y-3 text-sm">
                <Row k="Placement" v={`${invite.slot.label} · ${invite.run_label}`} />
                <Row k="Headline" v={form.title} />
                <Row k="Button" v={form.cta_label} />
                <Row k="Goes to" v={form.cta_type === 'internal' ? form.link_path : form.external_url} />
                <Row k="Cost" v={invite.free ? 'Complimentary' : `$${invite.price.toFixed(2)}`} />
              </dl>
              <Button
                onClick={submit}
                disabled={saving || (isMobileBuild() && !invite.free)}
                className="w-full bg-amber-400 py-6 text-slate-900 transition-colors duration-200 hover:bg-amber-300"
                data-testid="advertiser-submit-btn"
              >
                {saving ? <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  : invite.free ? 'Submit for approval' : 'Continue to payment'}
              </Button>
              {isMobileBuild() && !invite.free && (
                <div className="mt-5">
                  <UpgradeOnWebNotice
                    title="Pay for this placement on our website"
                    description="Open this same link on our website to pay and submit. Everything you have filled in is saved with the link."
                    ctaLabel="Open on Website"
                    path={`/advertise/${token}`}
                    testId="advertiser-web-notice-review"
                    showQr={false}
                  />
                </div>
              )}
            </Section>
          )}

          {step === 5 && clientSecret && (
            <Section title="Payment" hint="Your card is handled by Stripe. We never see the number.">
              {isMobileBuild() ? (
                <UpgradeOnWebNotice
                  title="Finish this on our website"
                  description="Ad placements are paid for on our website. Open this same form there, pay once, and your placement carries straight through."
                  ctaLabel="Open on Website"
                  path={`/advertise/${token}`}
                  testId="advertiser-web-notice"
                />
              ) : (
                <Elements
                  stripe={stripePromise}
                  options={{ clientSecret, appearance: { theme: 'night', variables: { colorPrimary: '#fbbf24' } } }}
                >
                  <PayForm amount={invite.price} onPaid={afterPayment} />
                </Elements>
              )}
            </Section>
          )}

          {step < 5 && (
            <div className="mt-10 flex items-center justify-between border-t border-white/10 pt-6">
              <Button
                variant="ghost"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                className="text-white/60 hover:text-white disabled:opacity-25"
                data-testid="advertiser-back-btn"
              >
                <ChevronLeft className="mr-1 h-4 w-4" /> Back
              </Button>
              {step < 4 && (
                <Button
                  onClick={() => setStep((s) => s + 1)}
                  disabled={!stepValid}
                  className="bg-white/10 text-white transition-colors duration-200 hover:bg-white/20 disabled:opacity-30"
                  data-testid="advertiser-next-btn"
                >
                  Next <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              )}
            </div>
          )}
        </div>

        {/* live preview */}
        <aside className="lg:sticky lg:top-12 lg:self-start">
          <p className="mb-3 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/40">
            <ImageIcon className="h-3.5 w-3.5" /> Live preview
          </p>
          <SpotlightCreative p={form} variant={invite.slot.variant}
            theme={invite.slot.theme || 'dark'} onImageError={() => setImageBad(true)} />
          {imageBad && (
            <p className="mt-3 rounded-lg border border-red-400/30 bg-red-500/10 p-3 text-xs text-red-200"
               data-testid="advertiser-image-broken">
              That image link doesn&apos;t load, so your ad would run with no picture. Open the link
              in a browser tab — if it doesn&apos;t show the image on its own, it isn&apos;t a direct
              image address.
            </p>
          )}
          {spec && (
            <p className="mt-4 text-xs leading-relaxed text-white/35">
              {spec.width} × {spec.height}px · {spec.ratio} · max {spec.max_kb}KB
            </p>
          )}
        </aside>
      </div>
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div
    className="min-h-screen px-6 py-16 sm:px-12 lg:px-24"
    style={{ background: 'linear-gradient(to bottom, #0a0e1a 0%, #1a2332 60%, #2d3748 100%)' }}
  >
    <div className="mx-auto max-w-5xl" data-testid="advertiser-form-page">{children}</div>
  </div>
);

const Section = ({ title, hint, children }) => (
  <div>
    <h2 className="mb-2 text-base font-semibold text-white md:text-lg">{title}</h2>
    {hint && <p className="mb-7 max-w-lg text-sm leading-relaxed text-gray-300">{hint}</p>}
    <div className="space-y-5">{children}</div>
  </div>
);

const Field = ({ label, hint, optional, children }) => (
  <div>
    <Label className="mb-2 block text-sm text-white/70">
      {label} {optional && <span className="text-white/35">— optional</span>}
    </Label>
    {children}
    {hint && <p className="mt-1.5 text-xs text-white/35">{hint}</p>}
  </div>
);

const Choice = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`rounded-full px-4 py-2 text-sm transition-colors duration-200 ${
      active ? 'bg-amber-400 text-slate-900' : 'border border-white/15 text-white/70 hover:border-white/40'
    }`}
  >
    {children}
  </button>
);

const Row = ({ k, v }) => (
  <div className="flex items-start justify-between gap-6 border-b border-white/5 pb-3">
    <dt className="text-white/45">{k}</dt>
    <dd className="max-w-[60%] break-words text-right text-white">{v || '—'}</dd>
  </div>
);

const Stat = ({ label, value, icon }) => (
  <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
    <p className="mb-1 flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-white/40">
      {icon} {label}
    </p>
    <p className="text-2xl text-white">{value}</p>
  </div>
);
